import { ChangeDetectorRef, Directive } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { DynamicPageData } from '../../models/dynamic-page-data';
import { QuoteControllerSelectors } from '../../store/quote-controller/quote-controller.module';
import { BrandNames } from '../../enums/brand-names';
import { AppConfigService } from '../../services/app-config.service';
import { ModuleInfo } from '../../models/module-info';
import { CmsSelectors } from '../../store/cms/cms.module';

@Directive()
@UntilDestroy()
export class BaseDynamicPageComponent {
    brand: BrandNames;
    data$ = this.store$.select(QuoteControllerSelectors.selectActivePage);
    loading = this.store$.selectSignal(CmsSelectors.selectIsLoading);
    activePage: DynamicPageData;

    constructor(
        public appConfig: AppConfigService,
        public router: Router,
        public store$: Store,
        public changeRef: ChangeDetectorRef,
    ) {
        this.brand = appConfig.config()?.brand;
        this.data$.pipe(untilDestroyed(this)).subscribe((el) => {
            if (this.activePage) {
                if (
                    this.activePage?.route?.baseRoute !== el.section?.baseRoute ||
                    this.activePage?.section.route !== el.section.route ||
                    this.activePage.page?.route !== el.page?.route
                ) {
                    this.activePage = el;
                    this.changeRef.markForCheck();
                }
            } else {
                this.activePage = el;
                this.changeRef.markForCheck();
            }
        });
    }

    getModuleLoader: (moduleInfo: ModuleInfo) => Promise<any> = (moduleInfo: ModuleInfo) => {
        return Promise.resolve(undefined);
    };

    /**
     * Component Map for Standalone components, any components added here can be loaded entirely on their own without
     * the need to add them into a module
     * Note: We do this ideally only for entry points where we want the load time as small as possible, otherwise loading
     * a whole module at a time is the preferred method of attack
     */
    componentMap: Record<string, () => Promise<any>> = {};

    /**
     * Asynchronously loads a standalone component based on the provided module information.
     *
     * @param {ModuleInfo} moduleInfo - The information about the module, including the name of the standalone component to load.
     * @returns {Promise<any>} A promise that resolves to the loaded component, or `undefined` if the component is not found in the `componentMap`.
     *
     * Usage:
     * This method retrieves the corresponding import function from the `componentMap` using the `standaloneComponent` property
     * of the `moduleInfo` object. If the import function exists, it is invoked to dynamically load the component.
     */
    getStandaloneComponent = async (moduleInfo: ModuleInfo): Promise<any> => {
        const importFunction = this.componentMap[moduleInfo.standaloneComponent];
        return importFunction ? await importFunction() : undefined;
    };
}
